/**
 * Google Drive appDataFolder 구현 (Drive REST v3).
 * - 앱 전용 숨김 공간만 사용 (drive.appdata 범위). 사용자의 일반 Drive 파일은 보거나 바꿀 수 없다.
 * - 올리는 내용은 모두 암호문 JSON이다.
 * - 일시적 오류(429·5xx·네트워크)는 짧게 재시도, 인증 만료(401)는 RemoteAuthError로 알린다.
 */
import { RemoteAuthError, RemoteUnavailableError, type RemoteFile, type RemoteStore } from './remote';

const API = 'https://www.googleapis.com/drive/v3/files';
const UPLOAD = 'https://www.googleapis.com/upload/drive/v3/files';

type DriveFile = { id: string; name: string; modifiedTime: string; size?: string };

export class GoogleDriveStore implements RemoteStore {
  private ids = new Map<string, string>();

  constructor(
    private readonly getToken: () => Promise<string>,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async request(url: string, init: RequestInit = {}, attempt = 0): Promise<Response> {
    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        ...init,
        headers: { ...(init.headers as Record<string, string>), Authorization: `Bearer ${await this.getToken()}` },
      });
    } catch (e) {
      if (e instanceof RemoteAuthError) throw e;
      if (attempt < 2) return this.retry(url, init, attempt);
      throw new RemoteUnavailableError();
    }
    if (res.status === 401) throw new RemoteAuthError();
    if ((res.status === 429 || res.status >= 500) && attempt < 3) return this.retry(url, init, attempt);
    return res;
  }

  private async retry(url: string, init: RequestInit, attempt: number) {
    await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
    return this.request(url, init, attempt + 1);
  }

  private async listAll(): Promise<DriveFile[]> {
    const out: DriveFile[] = [];
    let pageToken: string | undefined;
    do {
      const q = new URLSearchParams({
        spaces: 'appDataFolder',
        fields: 'nextPageToken,files(id,name,modifiedTime,size)',
        pageSize: '1000',
        orderBy: 'modifiedTime desc',
        ...(pageToken ? { pageToken } : {}),
      });
      const res = await this.request(`${API}?${q}`);
      if (!res.ok) throw new RemoteUnavailableError(`Drive 목록 실패 (${res.status})`);
      const body = (await res.json()) as { files: DriveFile[]; nextPageToken?: string };
      out.push(...body.files);
      pageToken = body.nextPageToken;
    } while (pageToken);
    // 같은 이름이 여러 개면 가장 최근 것을 쓴다 (목록은 최신순)
    this.ids.clear();
    for (const f of out) if (!this.ids.has(f.name)) this.ids.set(f.name, f.id);
    return out;
  }

  async list(prefix: string): Promise<RemoteFile[]> {
    const all = await this.listAll();
    const seen = new Set<string>();
    return all
      .filter((f) => f.name.startsWith(prefix) && !seen.has(f.name) && seen.add(f.name))
      .map((f) => ({
        name: f.name,
        id: f.id,
        modifiedTime: f.modifiedTime,
        ...(f.size ? { size: Number(f.size) } : {}),
      }));
  }

  private async idOf(name: string): Promise<string | undefined> {
    if (!this.ids.has(name)) await this.listAll();
    return this.ids.get(name);
  }

  async read(name: string): Promise<string | null> {
    const id = await this.idOf(name);
    if (!id) return null;
    const res = await this.request(`${API}/${id}?alt=media`);
    if (res.status === 404) {
      this.ids.delete(name);
      return null;
    }
    if (!res.ok) throw new RemoteUnavailableError(`Drive 읽기 실패 (${res.status})`);
    return res.text();
  }

  async remove(name: string): Promise<void> {
    const id = await this.idOf(name);
    if (!id) return;
    const res = await this.request(`${API}/${id}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 404) throw new RemoteUnavailableError(`Drive 지우기 실패 (${res.status})`);
    this.ids.delete(name);
  }

  async write(name: string, content: string): Promise<void> {
    const id = await this.idOf(name);
    if (id) {
      const res = await this.request(`${UPLOAD}/${id}?uploadType=media`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: content,
      });
      if (res.ok) return;
      if (res.status !== 404) throw new RemoteUnavailableError(`Drive 쓰기 실패 (${res.status})`);
      this.ids.delete(name);
    }
    const boundary = `counselai-${crypto.getRandomValues(new Uint32Array(2)).join('')}`;
    const body =
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
      JSON.stringify({ name, parents: ['appDataFolder'], mimeType: 'application/json' }) +
      `\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n${content}\r\n--${boundary}--`;
    const res = await this.request(`${UPLOAD}?uploadType=multipart&fields=id`, {
      method: 'POST',
      headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
      body,
    });
    if (!res.ok) throw new RemoteUnavailableError(`Drive 만들기 실패 (${res.status})`);
    const created = (await res.json()) as { id: string };
    this.ids.set(name, created.id);
  }
}
